import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Controller, useForm } from "react-hook-form";
import { Column, Row, Spacer } from "../../../../components/Layout/Layout";
import {
  createAlarmHandling,
  useRespondentGroups,
} from "../../../../core/api/responsecenters/responsecenters";
import { NewAlarmHandlingParams } from "../../../../core/api/responsecenters/types";
import useTranslations from "../../../../core/i18n/useTranslations";
import useUser from "../../../../core/user/useUser";
import { notify } from "../../../../ui-lib/components/Alerts/Toast";
import Dropdown from "../../../../ui-lib/components/Dropdown/Dropdown";
import TextInput from "../../../../ui-lib/components/Inputs/TextInput";
import LoadingSpinner from "../../../../ui-lib/components/Loading/LoadingSpinner";
import { notifyApiErrors } from "../../../../core/helpers/helpers";
import PrimeModal from "../../../../ui-lib/components/PrimeModal/PrimeModal";

const CreateAlarmHandlingModal = ({
  responseCenterId,
  open,
  onAdd,
  onClose,
}: {
  responseCenterId: number | string;
  open: boolean;
  onAdd: () => void;
  onClose: () => void;
}) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();
  const { data: respondentGroups, isLoading } =
    useRespondentGroups(responseCenterId);

  const {
    control,
    formState: { isSubmitting, errors },
    setValue,
    handleSubmit,
  } = useForm<NewAlarmHandlingParams>();

  const onSubmit = async (params: NewAlarmHandlingParams) => {
    try {
      await createAlarmHandling(responseCenterId, params, authenticatedRequest);
      notify({
        message: t("Messages:create_alarm_handling_success"),
      });
      onAdd();
      onClose();
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  useEffect(() => {
    if (respondentGroups?.length > 0) {
      setValue("primaryRespondentGroupId", respondentGroups[0].id);
    }
  }, [respondentGroups]);

  return (
    <PrimeModal
      withFooter
      withHeader
      fixedWidth="848px"
      header={t("AlarmReceptions:alarm_handling_add")}
      isOpen={open}
      onClose={onClose}
      loading={isSubmitting}
      submitBtn={{
        text: t("AlarmReceptions:alarm_handling_create"),
        onClick: handleSubmit(onSubmit),
      }}
      cancelBtn={{
        text: t("Common:cancel"),
        onClick: onClose,
      }}
    >
      <p className="grey-600">
        {t("AlarmReceptions:alarm_handling_add_description")}
      </p>

      <Spacer size={16} />

      {isLoading ? (
        <LoadingSpinner theme="primary" />
      ) : (
        <form>
          <Row align="start">
            <Column>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextInput
                    required
                    label={t("Common:name")}
                    placeholder={t("AlarmReceptions:handling_name_placeholder")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors.name && t("Errors:input_field_required")
                    }
                  />
                )}
              />
            </Column>
            <Spacer size={32} />
            <Column>
              <Controller
                name="primaryRespondentGroupId"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => {
                  const dropdownItems = respondentGroups.map(
                    (respondentGroup) => ({
                      id: respondentGroup.id,
                      name: respondentGroup.description,
                      isSelected: respondentGroup.id === field.value,
                    })
                  );

                  return (
                    <Dropdown
                      required
                      title={t(
                        "AlarmReceptions:alarm_handling_input_respondent_group_primary"
                      )}
                      width="376px"
                      selectedItem={dropdownItems.find((i) => i.isSelected)}
                      items={dropdownItems}
                      onSelectItem={(item) =>
                        setValue(field.name, item.id as number)
                      }
                      validationError={
                        errors.primaryRespondentGroupId &&
                        t("Errors:input_field_required")
                      }
                      showTooltip
                    />
                  );
                }}
              />
            </Column>
          </Row>
        </form>
      )}
    </PrimeModal>
  );
};

export default observer(CreateAlarmHandlingModal);
